import axios from "axios";
import React, { useEffect, useState } from "react";
import { Button, Form, Spinner } from "react-bootstrap";
import { Step, Stepper } from "react-form-stepper";
import { getURLbyEndPointV2 } from "../../../../../store/api";
import { getAuthUserlocal } from "../../../../../store/services";
import AreaOfExpertise from "./AreaOfExpertise";
import EditB2bRegistration from "./EditB2bRegistration";
import ScheduleCalender from "./ScheduleCalender";
import SelectIndustry from "./SelectIndustry";
import Summary from "./Summary";
import UserInformation from "./UserInformation";
import toast from 'react-hot-toast';

const B2bRegistration = ({ onEditClick }) => {
    const [activeStep, setActiveStep] = useState(0);
    const [errors, setErrors] = useState({});
    const [isLoading, setIsLoading] = useState(false);
    const [validated, setValidated] = useState(false);
    const [skippedMonths, setSkippedMonths] = useState([]);
    const [registrationData, setRegistrationData] = useState(null);

    const [formData, setFormData] = useState({
        participants: [],
        industries: [],
        expertise: {},
        selectedTimeSlots: [],
    });

    const [termsAccepted, setTermsAccepted] = useState({
        terms: false,
        rules: false,
        freeMeeting: false,
        knowledgeSharing: false,
        futureServices: false,
    });

    const userId = getAuthUserlocal()._id;

    useEffect(() => {
        const fetchRegistrationData = async () => {
            try {
                const regData = await axios.get(getURLbyEndPointV2("getB2bRegistration") + userId);
                setRegistrationData(regData.data.data);
            } catch (error) {
                console.error("Error fetching registration data:", error);
            }
        };
        fetchRegistrationData();
    }, [userId]);

    const steps = [
        { component: <UserInformation formData={formData} setFormData={setFormData} error={errors.participants} /> },
        { component: <SelectIndustry formData={formData} setFormData={setFormData} error={errors.industry} /> },
        { component: <AreaOfExpertise formData={formData} setFormData={setFormData} error={errors.expertise} /> },
        {
            component: (
                <ScheduleCalender
                    formData={formData}
                    setFormData={setFormData}
                    error={errors.schedule}
                    skippedMonths={skippedMonths}
                    setSkippedMonths={setSkippedMonths}
                />
            ),
        },
        { component: <Summary formData={formData} setActiveStep={setActiveStep} /> },
    ];

    const validateTimeSlots = () => {
        const slotsByMonth = {};
        const slotsByDay = {};

        for (const slot of formData.selectedTimeSlots) {
            const date = new Date(slot.date);
            const monthKey = `${date.getFullYear()}-${date.getMonth()}`;
            const dayKey = date.toDateString();

            slotsByMonth[monthKey] = slotsByMonth[monthKey] || [];
            slotsByDay[dayKey] = slotsByDay[dayKey] || [];

            slotsByMonth[monthKey].push(slot);
            slotsByDay[dayKey].push(slot);
        }

        for (const day in slotsByDay) {
            if (slotsByDay[day].length > 2) {
                return "Maximum 2 slots per day allowed";
            }
        }

        for (const month in slotsByMonth) {
            if (slotsByMonth[month].length < 8) {
                return "Minimum 8 slots per month allowed";
            }
        }

        if (Object.keys(slotsByMonth).length === 0) {
            return "Please select slots for at least one month";
        }

        return null;
    };

    const validateStep = (step) => {
        let isValid = true;
        const newErrors = {};

        switch (step) {
            case 0:
                if (formData.participants.length === 0) {
                    toast.error("Please add at least one participant");
                    newErrors.participants = "Please add at least one participant";
                    isValid = false;
                }
                break;
            case 1:
                if (formData.industries.length === 0) {
                    toast.error("Please select at least one industry");
                    newErrors.industry = "Please select at least one industry";
                    isValid = false;
                }
                break;
            case 2:
                const selectedAreas = Object.keys(formData.expertise).filter(
                    (section) => formData.expertise[section] && formData.expertise[section].length > 0
                );
                if (selectedAreas.length !== 4) {
                    toast.error(`Please select minimum 4 areas of expertise. Currently selected: ${selectedAreas.length}`);
                    newErrors.expertise = `Please select minimum 4 areas of expertise. Currently selected: ${selectedAreas.length}`;
                    isValid = false;
                    break;
                }
                for (const section of selectedAreas) {
                    const servicesCount = formData.expertise[section].length;
                    if (servicesCount < 1 || servicesCount > 6) {
                        newErrors.expertise = `Each selected area must have between 1 and 6 services. ${section} has ${servicesCount} services.`;
                        isValid = false;
                        break;
                    }
                    // if (formData.expertise[section].includes("Others")) {
                    //     const additionalServices = formData.expertise[section].filter((service) => service !== "Others").length;
                    //     if (additionalServices > 3) {
                    //         newErrors.expertise = `You can only add up to 3 additional services in ${section}. Currently added: ${additionalServices}`;
                    //         isValid = false;
                    //         break;
                    //     }
                    // }
                }
                break;
            case 3:
                const timeSlotError = validateTimeSlots();
                if (timeSlotError) {
                    toast.error(timeSlotError);
                    newErrors.schedule = timeSlotError;
                    isValid = false;
                }
                break;
            default:
                break;
        }
        setErrors(newErrors);
        return isValid;
    };

    const handleNext = () => {
        if (validateStep(activeStep)) {
            setActiveStep((prevStep) => prevStep + 1);
            setErrors({});
        }
    };

    const handleBack = () => {
        setActiveStep((prevStep) => prevStep - 1);
        setErrors({});
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const form = e.currentTarget;

        if (!form.checkValidity() || !Object.values(termsAccepted).every(Boolean)) {
            e.stopPropagation();
            toast.error("Please accept all terms and conditions");
            setValidated(true);
            return;
        }

        if (!validateStep(3)) {
            return;
        }

        setIsLoading(true);

        try {
            const res = await axios.post(getURLbyEndPointV2("createB2bRegistration") + userId, {
                formData,
            });

            if (res.data.success) {
                alert(res.data.message);
                window.location.reload();
            } else {
                alert("Registration failed: " + res.data.message);
            }
        } catch (error) {
            if (error.response) {
                const { status, data } = error.response;
                if (status === 404) {
                    toast.error(data.message || "Client not found");
                } else if (status === 400) {
                    toast.error(data.message || "You have already registered for the event");
                } else {
                    toast.error("An error occurred while submitting the form.");
                }
            } else {
                toast.error("Network error. Please try again.");
            }
        } finally {
            setIsLoading(false);
        }
    };

    const renderTermsAndConditions = () => (
        <Form noValidate validated={validated} onSubmit={handleSubmit} className="w-100">
            <div className="mx-auto">
                <h6 className="fw-bold mb-3">Before Proceeding:</h6>
                <Form.Group className="mb-3 fs-5 mx-auto">
                    {Object.keys(termsAccepted).map((key, index) => (
                        <Form.Check
                            key={index}
                            size="md"
                            required
                            type="checkbox"
                            id={`terms-${key}`}
                            label={
                                <span style={{ fontSize: "1.1rem" }}>
                                    {key === "terms"
                                        ? "I confirm that I have read and understood the terms and conditions of the B2B forum."
                                        : key === "rules"
                                            ? "I agree to abide by the rules and regulations set by the B2B forum."
                                            : key === "freeMeeting"
                                                ? "I acknowledge and understand that my first meeting with an Indian business participant is free of cost."
                                                : key === "knowledgeSharing"
                                                    ? "I understand that, although this meeting is not intended to provide buyers, conduct detailed business research, or address trade disputes or IPR issues, I agree to share basic and important knowledge on the points requested by the participant."
                                                    : "I acknowledge that I will be available for future services, including detailed research or additional trade services, at a reasonable fee after this session."}
                                </span>
                            }
                            checked={termsAccepted[key]}
                            onChange={(e) => setTermsAccepted({ ...termsAccepted, [key]: e.target.checked })}
                            feedback="You must agree to this term before submitting."
                            feedbackType="invalid"
                        />
                    ))}
                </Form.Group>
            </div>
            <div className="d-flex justify-content-between">
                <Button variant="secondary" onClick={handleBack} disabled={activeStep === 0} size="sm">
                    Back
                </Button>
                <Button type="submit" size="sm" disabled={isLoading}>
                    {isLoading ? (
                        <>
                            <Spinner as="span" animation="grow" size="sm" role="status" aria-hidden="true" className="me-2" />
                            Loading...
                        </>
                    ) : (
                        "Submit"
                    )}
                </Button>
            </div>
        </Form>
    );

    return (
        <div>
            <Stepper
                activeStep={activeStep}
                connectorStateColors={true}
                styleConfig={{
                    activeBgColor: "#2f67f8",
                    completedBgColor: "#28a745",
                    inactiveBgColor: "#d6d6d6",
                    inactiveTextColor: "#6c757d",
                    activeTextColor: "#fff",
                    completedTextColor: "#fff",
                }}
                connectorStyleConfig={{
                    activeColor: "#28a745",
                    completedColor: "#28a745",
                    disabledColor: "#d6d6d6",
                }}
            >
                {steps.map((_, index) => (
                    <Step key={index} />
                ))}
            </Stepper>

            <div className="mt-4">
                {React.cloneElement(steps[activeStep].component, {
                    formData,
                    setFormData,
                    error: errors[Object.keys(errors)[0]],
                })}
                <div>
                    {activeStep !== steps.length - 1 && (
                        <div className="d-flex mx-auto justify-content-between">
                            <div className="d-flex gap-2">
                                {registrationData && onEditClick && (
                                    <Button variant="primary" onClick={onEditClick} size="sm">
                                        Edit Registration Details
                                    </Button>
                                )}
                                <Button variant="secondary" onClick={handleBack} size="sm" disabled={activeStep === 0}>
                                    Back
                                </Button>
                            </div>
                            <Button variant="primary" size="sm" onClick={handleNext}>
                                Next
                            </Button>
                        </div>
                    )}
                    {activeStep === steps.length - 1 && <div className="px-4">{renderTermsAndConditions()}</div>}
                </div>
            </div>
        </div>
    );
};

export default B2bRegistration;